/**
 * RateLibraryAdmin — Admin panel for managing the MongoDB-backed rate card.
 *
 * 3 tabs:
 *   1. Rates            — list, filter, inline edit rate_gbp, archive, bulk CSV upload, CSV export
 *   2. Regional Factors — list and edit regional multipliers
 *   3. Rate History     — paginated change log + CSV export
 *
 * Parent wires this into AdminDashboard under the new "Rates" tab.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Download, Upload, RefreshCw, Archive, Edit2, Check, X, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RateLibraryAdmin = ({ token }) => {
  const [tab, setTab] = useState('rates');

  return (
    <div data-testid="rate-library-admin" className="card" style={{ padding: 0, overflow: 'hidden' }}>
      {/* Sub-tabs */}
      <div
        className="flex"
        style={{ borderBottom: '1px solid var(--color-border)', background: '#F8FAFC' }}
      >
        {[
          ['rates', 'Rates'],
          ['regions', 'Regional Factors'],
          ['history', 'Rate History'],
        ].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            data-testid={`ratelib-subtab-${id}`}
            className="px-4 py-3 text-sm font-semibold"
            style={{
              background: tab === id ? '#fff' : 'transparent',
              color: tab === id ? 'var(--color-navy)' : 'var(--color-muted)',
              borderBottom: tab === id ? '2px solid var(--color-navy)' : '2px solid transparent',
              cursor: 'pointer',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <div style={{ padding: 20 }}>
        {tab === 'rates' && <RatesTab token={token} />}
        {tab === 'regions' && <RegionsTab token={token} />}
        {tab === 'history' && <HistoryTab token={token} />}
      </div>
    </div>
  );
};

// ───────────────────────── Rates tab ─────────────────────────
const RatesTab = ({ token }) => {
  const [rates, setRates] = useState([]);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterTrade, setFilterTrade] = useState('');
  const [search, setSearch] = useState('');
  const [editingCode, setEditingCode] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvError, setCsvError] = useState('');

  const hdr = { Authorization: `Bearer ${token}` };

  const fetchRates = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/rates`, { headers: hdr });
      setRates(r.data.rates || []);
      setTrades(r.data.trades || []);
    } catch (e) {
      console.error('Rate library fetch failed', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRates();
    // eslint-disable-next-line
  }, []);

  const filtered = useMemo(() => {
    return rates.filter((r) => {
      if (filterTrade && r.trade !== filterTrade) return false;
      if (search) {
        const s = search.toLowerCase();
        return (
          (r.description || '').toLowerCase().includes(s) ||
          (r.rate_code || '').toLowerCase().includes(s)
        );
      }
      return true;
    });
  }, [rates, filterTrade, search]);

  const saveRate = async (code) => {
    const val = parseFloat(editValue);
    if (isNaN(val) || val < 0) {
      setEditingCode(null);
      return;
    }
    try {
      await axios.put(`${API}/admin/rates/${code}`, { rate_gbp: val }, { headers: hdr });
      setEditingCode(null);
      fetchRates();
    } catch (e) {
      console.error('Rate save failed', e);
      alert('Save failed — see console.');
    }
  };

  const archiveRate = async (code) => {
    if (!window.confirm(`Archive ${code}? The engine will fall back to its hardcoded default for this rate.`)) return;
    try {
      await axios.post(`${API}/admin/rates/${code}/archive`, {}, { headers: hdr });
      fetchRates();
    } catch (e) {
      console.error('Archive failed', e);
    }
  };

  const uploadCsv = async () => {
    if (!csvFile) return;
    setCsvError('');
    const form = new FormData();
    form.append('file', csvFile);
    try {
      const r = await axios.post(`${API}/admin/rates/bulk-upload`, form, {
        headers: { ...hdr, 'Content-Type': 'multipart/form-data' },
      });
      setCsvPreview(r.data);
      fetchRates();
    } catch (e) {
      setCsvError(e.response?.data?.error || 'Upload failed');
    }
  };

  const exportCsv = () => {
    window.open(`${API}/admin/rates/export`, '_blank');
  };

  return (
    <div>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div style={{ position: 'relative' }}>
          <Search className="w-4 h-4" style={{ position: 'absolute', left: 10, top: 10, color: '#94A3B8' }} />
          <input
            type="text"
            placeholder="Search rate or description"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="ratelib-search"
            style={{
              padding: '8px 12px 8px 34px',
              border: '1px solid var(--color-border)',
              borderRadius: 8,
              fontSize: 13,
              minWidth: 260,
            }}
          />
        </div>
        <select
          value={filterTrade}
          onChange={(e) => setFilterTrade(e.target.value)}
          data-testid="ratelib-trade-filter"
          style={{
            padding: '8px 12px',
            border: '1px solid var(--color-border)',
            borderRadius: 8,
            fontSize: 13,
          }}
        >
          <option value="">All trades</option>
          {trades.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <label
          className="btn-secondary flex items-center gap-2"
          style={{ cursor: 'pointer' }}
          data-testid="ratelib-upload-label"
        >
          <Upload className="w-4 h-4" />
          Upload Rate Card (CSV)
          <input
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={(e) => {
              setCsvFile(e.target.files?.[0] || null);
              setCsvPreview(null);
              setCsvError('');
            }}
            data-testid="ratelib-csv-input"
          />
        </label>
        {csvFile && (
          <button
            className="btn-primary"
            onClick={uploadCsv}
            data-testid="ratelib-csv-submit"
          >
            Apply {csvFile.name}
          </button>
        )}
        <button onClick={exportCsv} className="btn-secondary flex items-center gap-2" data-testid="ratelib-export-btn">
          <Download className="w-4 h-4" />
          Download CSV
        </button>
        <button onClick={fetchRates} className="btn-secondary flex items-center gap-2" data-testid="ratelib-refresh-btn">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {csvError && (
        <div style={{ background: '#FEF2F2', color: '#991B1B', border: '1px solid #FECACA', padding: 12, borderRadius: 8, marginBottom: 12 }}>
          {csvError}
        </div>
      )}

      {csvPreview && (
        <div style={{ background: '#F0FDF4', color: '#166534', border: '1px solid #BBF7D0', padding: 12, borderRadius: 8, marginBottom: 12 }}>
          <strong>Upload processed:</strong> {csvPreview.created} created, {csvPreview.updated} updated, {csvPreview.unchanged} unchanged.
          {csvPreview.errors?.length > 0 && (
            <div style={{ marginTop: 6, color: '#991B1B' }}>
              Errors ({csvPreview.errors.length}): {csvPreview.errors.slice(0, 3).map((e, i) => (
                <div key={i}>Row {e.row}: {e.error}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <div style={{ overflow: 'auto', border: '1px solid var(--color-border)', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }} data-testid="ratelib-table">
          <thead style={{ background: '#F1F5F9' }}>
            <tr>
              <th style={thStyle}>Trade</th>
              <th style={thStyle}>Rate code</th>
              <th style={{ ...thStyle, minWidth: 260 }}>Description</th>
              <th style={thStyle}>Unit</th>
              <th style={{ ...thStyle, textAlign: 'right' }} title="Value / low-spec tier">Low (£)</th>
              <th style={{ ...thStyle, textAlign: 'right' }} title="Standard / mid-spec tier — editable, also the default rate">Mid (£)</th>
              <th style={{ ...thStyle, textAlign: 'right' }} title="Premium / high-spec tier">High (£)</th>
              <th style={thStyle} title="Pricing source reference">Source</th>
              <th style={thStyle}>Updated</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.rate_code} style={{ borderTop: '1px solid #E2E8F0' }}>
                <td style={tdStyle}>{r.trade || '—'}</td>
                <td style={{ ...tdStyle, fontFamily: 'monospace', color: '#64748B' }}>{r.rate_code}</td>
                <td style={tdStyle}>
                  <div>{r.description}</div>
                  {r.keywords && r.keywords.length > 0 && (
                    <div style={{ marginTop: 3, display: 'flex', flexWrap: 'wrap', gap: 3 }} data-testid={`ratelib-keywords-${r.rate_code}`}>
                      {r.keywords.slice(0, 4).map((kw, i) => (
                        <span key={i} style={{ fontSize: 10, padding: '1px 6px', borderRadius: 999, background: '#EEF2FF', color: '#3730A3', border: '1px solid #C7D2FE' }}>
                          {kw}
                        </span>
                      ))}
                    </div>
                  )}
                  {(r.base_date || r.base_location) && (
                    <div style={{ marginTop: 3, fontSize: 10, color: '#64748B', fontStyle: 'italic' }}>
                      {r.base_date ? `Base: ${r.base_date}` : ''}
                      {r.base_date && r.base_location ? ' · ' : ''}
                      {r.base_location || ''}
                    </div>
                  )}
                </td>
                <td style={tdStyle}>{r.unit}</td>
                <td style={{ ...tdStyle, textAlign: 'right', color: '#64748B', fontSize: 12 }} data-testid={`ratelib-low-${r.rate_code}`}>
                  {r.low_gbp != null ? `£${Number(r.low_gbp).toFixed(2)}` : '—'}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>
                  {editingCode === r.rate_code ? (
                    <input
                      type="number"
                      step="0.01"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      autoFocus
                      data-testid={`ratelib-edit-input-${r.rate_code}`}
                      style={{
                        width: 100,
                        padding: '4px 8px',
                        border: '1px solid var(--color-navy)',
                        borderRadius: 6,
                        textAlign: 'right',
                        fontSize: 13,
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveRate(r.rate_code);
                        if (e.key === 'Escape') setEditingCode(null);
                      }}
                    />
                  ) : (
                    `£${Number(r.rate_gbp).toFixed(2)}`
                  )}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', color: '#64748B', fontSize: 12 }} data-testid={`ratelib-high-${r.rate_code}`}>
                  {r.high_gbp != null ? `£${Number(r.high_gbp).toFixed(2)}` : '—'}
                </td>
                <td style={{ ...tdStyle, color: '#475569', fontSize: 11, maxWidth: 120 }} title={r.source || ''} data-testid={`ratelib-source-${r.rate_code}`}>
                  {r.source ? (
                    <span style={{ display: 'inline-block', maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                      {r.source}
                    </span>
                  ) : '—'}
                </td>
                <td style={{ ...tdStyle, color: '#64748B', fontSize: 12 }}>
                  {r.updated_at ? new Date(r.updated_at).toLocaleDateString('en-GB') : '—'}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  {editingCode === r.rate_code ? (
                    <div className="flex gap-1 justify-end">
                      <button
                        onClick={() => saveRate(r.rate_code)}
                        data-testid={`ratelib-save-${r.rate_code}`}
                        className="icon-btn"
                        style={{ color: '#16A34A' }}
                        title="Save"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setEditingCode(null)}
                        data-testid={`ratelib-cancel-${r.rate_code}`}
                        className="icon-btn"
                        style={{ color: '#64748B' }}
                        title="Cancel"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => {
                          setEditingCode(r.rate_code);
                          setEditValue(String(r.rate_gbp));
                        }}
                        data-testid={`ratelib-edit-${r.rate_code}`}
                        className="icon-btn"
                        title="Edit Mid rate"
                        style={{ color: 'var(--color-navy)' }}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => archiveRate(r.rate_code)}
                        data-testid={`ratelib-archive-${r.rate_code}`}
                        className="icon-btn"
                        title="Archive"
                        style={{ color: '#DC2626' }}
                      >
                        <Archive className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && !loading && (
              <tr>
                <td colSpan={10} style={{ padding: 24, textAlign: 'center', color: '#94A3B8' }}>
                  No rates match.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: 10, fontSize: 11, color: '#64748B', fontStyle: 'italic' }}>
        Editing the Mid rate updates the default used when no spec tier is selected. Low/High are read-only here
        (edit via CSV upload). Rates with a base date (e.g. May 2020) are auto-uplifted at engine load time.
      </div>
    </div>
  );
};

// ───────────────────────── Regions tab ─────────────────────────
const RegionsTab = ({ token }) => {
  const [regions, setRegions] = useState([]);
  const [editingCode, setEditingCode] = useState(null);
  const [editValue, setEditValue] = useState('');
  const hdr = { Authorization: `Bearer ${token}` };

  const fetchRegions = async () => {
    try {
      const r = await axios.get(`${API}/admin/regional-factors`, { headers: hdr });
      setRegions(r.data.regions || []);
    } catch (e) {
      console.error('Regions fetch failed', e);
    }
  };
  useEffect(() => {
    fetchRegions();
    // eslint-disable-next-line
  }, []);

  const save = async (code) => {
    const val = parseFloat(editValue);
    if (isNaN(val) || val <= 0) {
      setEditingCode(null);
      return;
    }
    try {
      await axios.put(`${API}/admin/regional-factors/${code}`, { factor: val }, { headers: hdr });
      setEditingCode(null);
      fetchRegions();
    } catch (e) {
      console.error('Region save failed', e);
      alert('Save failed');
    }
  };

  return (
    <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }} data-testid="ratelib-regions-table">
        <thead style={{ background: '#F1F5F9' }}>
          <tr>
            <th style={thStyle}>Region</th>
            <th style={thStyle}>Code</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Factor</th>
            <th style={thStyle}>Updated</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {regions.map((r) => (
            <tr key={r.region_code} style={{ borderTop: '1px solid #E2E8F0' }}>
              <td style={tdStyle}>{r.region_name}</td>
              <td style={{ ...tdStyle, fontFamily: 'monospace', color: '#64748B' }}>{r.region_code}</td>
              <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>
                {editingCode === r.region_code ? (
                  <input
                    type="number"
                    step="0.001"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    autoFocus
                    data-testid={`ratelib-region-edit-input-${r.region_code}`}
                    style={{
                      width: 90,
                      padding: '4px 8px',
                      border: '1px solid var(--color-navy)',
                      borderRadius: 6,
                      textAlign: 'right',
                      fontSize: 13,
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') save(r.region_code);
                      if (e.key === 'Escape') setEditingCode(null);
                    }}
                  />
                ) : (
                  `×${Number(r.factor).toFixed(3)}`
                )}
              </td>
              <td style={{ ...tdStyle, color: '#64748B', fontSize: 12 }}>
                {r.updated_at ? new Date(r.updated_at).toLocaleDateString('en-GB') : '—'}
              </td>
              <td style={{ ...tdStyle, textAlign: 'right' }}>
                {editingCode === r.region_code ? (
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => save(r.region_code)} className="icon-btn" data-testid={`ratelib-region-save-${r.region_code}`} style={{ color: '#16A34A' }}>
                      <Check className="w-4 h-4" />
                    </button>
                    <button onClick={() => setEditingCode(null)} className="icon-btn" style={{ color: '#64748B' }}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => { setEditingCode(r.region_code); setEditValue(String(r.factor)); }}
                    className="icon-btn"
                    data-testid={`ratelib-region-edit-${r.region_code}`}
                    title="Edit factor"
                    style={{ color: 'var(--color-navy)' }}
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ───────────────────────── History tab ─────────────────────────
const HistoryTab = ({ token }) => {
  const [history, setHistory] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;
  const hdr = { Authorization: `Bearer ${token}` };

  const fetchHistory = async (p = 0) => {
    try {
      const r = await axios.get(`${API}/admin/rates/history`, {
        headers: hdr,
        params: { limit: PAGE_SIZE, skip: p * PAGE_SIZE },
      });
      setHistory(r.data.history || []);
      setTotal(r.data.total || 0);
      setPage(p);
    } catch (e) {
      console.error('History fetch failed', e);
    }
  };
  useEffect(() => {
    fetchHistory(0);
    // eslint-disable-next-line
  }, []);

  const maxPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <div style={{ fontSize: 13, color: '#475569' }}>
          {total} change{total !== 1 ? 's' : ''} logged
        </div>
        <button
          onClick={() => window.open(`${API}/admin/rates/history/export`, '_blank')}
          className="btn-secondary flex items-center gap-2"
          data-testid="ratelib-history-export"
        >
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>
      <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }} data-testid="ratelib-history-table">
          <thead style={{ background: '#F1F5F9' }}>
            <tr>
              <th style={thStyle}>When</th>
              <th style={thStyle}>Rate code</th>
              <th style={thStyle}>Description</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Old</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>New</th>
              <th style={thStyle}>Action</th>
              <th style={thStyle}>By</th>
            </tr>
          </thead>
          <tbody>
            {history.map((h, i) => (
              <tr key={i} style={{ borderTop: '1px solid #E2E8F0' }}>
                <td style={{ ...tdStyle, color: '#64748B', fontSize: 12 }}>
                  {h.changed_at ? new Date(h.changed_at).toLocaleString('en-GB') : '—'}
                </td>
                <td style={{ ...tdStyle, fontFamily: 'monospace', color: '#64748B' }}>{h.rate_code}</td>
                <td style={tdStyle}>{h.description}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  {h.old_rate != null ? `£${Number(h.old_rate).toFixed(2)}` : '—'}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>
                  {h.new_rate != null ? `£${Number(h.new_rate).toFixed(2)}` : '—'}
                </td>
                <td style={tdStyle}>{h.action || 'update'}</td>
                <td style={tdStyle}>{h.changed_by}</td>
              </tr>
            ))}
            {history.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#94A3B8' }}>
                  No changes yet — edit a rate or upload a CSV to see history here.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {maxPage > 0 && (
        <div className="flex justify-end gap-2 mt-3">
          <button
            disabled={page === 0}
            onClick={() => fetchHistory(page - 1)}
            className="btn-secondary"
            style={{ opacity: page === 0 ? 0.5 : 1 }}
            data-testid="ratelib-history-prev"
          >
            Previous
          </button>
          <span style={{ padding: '8px 12px', fontSize: 13, color: '#475569' }}>
            Page {page + 1} of {maxPage + 1}
          </span>
          <button
            disabled={page >= maxPage}
            onClick={() => fetchHistory(page + 1)}
            className="btn-secondary"
            style={{ opacity: page >= maxPage ? 0.5 : 1 }}
            data-testid="ratelib-history-next"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

const thStyle = {
  padding: '10px 12px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 700,
  color: '#475569',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
};
const tdStyle = { padding: '10px 12px', color: '#0F172A' };

export default RateLibraryAdmin;
